(() => {
  const SELECTOR = '[data-lf-variant-assist]';

  const humanize = (value) => String(value || '')
    .replace(/_/g, ' · ')
    .replace(/(\d)\s*ml\b/gi, '$1 ml')
    .replace(/\s+/g, ' ')
    .trim();

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR',
        maximumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return '₹' + Math.round(Number(cents || 0) / 100).toLocaleString('en-IN');
    }
  };

  const initialize = (root) => {
    if (!root || root.dataset.lfReady === 'true') return;
    const form = root.closest('form[data-type="add-to-cart-form"]');
    const jsonNode = form && form.querySelector('.pr_variants_json');
    const idInput = form && form.querySelector('input[name="id"]');
    if (!form || !jsonNode || !idInput) {
      root.hidden = true;
      return;
    }

    let variants = [];
    try {
      variants = JSON.parse(jsonNode.textContent || '[]');
    } catch (_) {
      root.hidden = true;
      return;
    }
    if (!variants.length) {
      root.hidden = true;
      return;
    }

    root.dataset.lfReady = 'true';
    const currency = root.dataset.currency || 'INR';
    const nameNode = root.querySelector('[data-lf-variant-name]');
    const priceNode = root.querySelector('[data-lf-variant-price]');
    const savingNode = root.querySelector('[data-lf-variant-saving]');
    const availabilityNode = root.querySelector('[data-lf-variant-availability]');
    const availabilityText = availabilityNode && availabilityNode.querySelector('strong');
    const imageNode = root.querySelector('[data-lf-variant-image]');
    const liveNode = root.querySelector('[data-lf-variant-live]');
    const shareButton = root.querySelector('[data-lf-share-selection]');
    const helpLink = root.querySelector('[data-lf-variant-help]');
    let lastVariantId = null;
    let updateTimer = null;

    const currentVariant = () => {
      const optionRoots = Array.from(form.querySelectorAll('[data-swatch-option]'));
      const selectedOptions = optionRoots.map((optionRoot) => {
        const selected = optionRoot.querySelector('[data-swatch-item].is--selected');
        const checked = optionRoot.querySelector('input[type="radio"]:checked');
        return selected ? selected.getAttribute('data-value') : checked ? checked.value : '';
      }).filter(Boolean);

      if (selectedOptions.length) {
        const matched = variants.find((variant) =>
          selectedOptions.every((value, index) => String((variant.options || [])[index]) === String(value))
        );
        if (matched) return matched;
      }

      const id = String(idInput.value || '');
      return variants.find((variant) => String(variant.id) === id) || variants.find((variant) => variant.available) || variants[0];
    };

    const cleanSwatches = () => {
      form.querySelectorAll('[data-swatch-item]').forEach((item) => {
        const raw = item.getAttribute('data-value') || item.textContent;
        const label = humanize(raw);
        const soldOut = item.classList.contains('is--soldout') || item.classList.contains('is--unavailable');
        item.setAttribute('aria-label', label + (soldOut ? ', unavailable' : ''));
        item.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
        item.setAttribute('tabindex', soldOut ? '-1' : '0');
        if (!item.classList.contains('is-sw__color') && item.textContent.trim() !== label) {
          item.textContent = label;
        }
        if (!item.dataset.lfKeyboard) {
          item.dataset.lfKeyboard = 'true';
          item.addEventListener('keydown', (event) => {
            if ((event.key === 'Enter' || event.key === ' ') && item.getAttribute('aria-disabled') !== 'true') {
              event.preventDefault();
              item.click();
            }
          });
        }
      });
      form.querySelectorAll('[data-current-value]').forEach((item) => {
        const cleaned = humanize(item.textContent);
        if (cleaned) item.textContent = cleaned;
      });
    };

    const sync = (announce) => {
      cleanSwatches();
      const variant = currentVariant();
      if (!variant) return;

      const variantId = String(variant.id);
      if (String(idInput.value) !== variantId) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const optionLabel = (variant.options || [variant.public_title || variant.title])
        .filter(Boolean)
        .map(humanize)
        .join(' · ');
      const compareAt = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const saving = Math.max(0, compareAt - price);
      const image = variant.featured_image && variant.featured_image.src
        ? variant.featured_image.src
        : variant.featured_media && variant.featured_media.preview_image
          ? variant.featured_media.preview_image.src
          : '';

      if (nameNode) nameNode.textContent = optionLabel || humanize(variant.title);
      if (priceNode) priceNode.textContent = money(price, currency);
      if (savingNode) {
        savingNode.hidden = saving <= 0;
        savingNode.textContent = saving > 0
          ? 'Save ' + money(saving, currency) + ' (' + Math.round((saving / compareAt) * 100) + '%)'
          : '';
      }
      if (availabilityNode) availabilityNode.dataset.state = variant.available ? 'available' : 'soldout';
      if (availabilityText) availabilityText.textContent = variant.available ? 'Ready to add' : 'Currently unavailable';
      if (imageNode && image) imageNode.src = image.startsWith('//') ? location.protocol + image : image;

      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      if (location.pathname.indexOf('/products/') !== -1 && history.replaceState) {
        history.replaceState(history.state, '', url.toString());
      }

      if (helpLink) {
        const message = 'Hi LA’ FORTE, please help me choose this option: ' +
          (root.dataset.productTitle || document.title) + ' — ' + optionLabel + '. ' + url.toString();
        helpLink.href = 'https://wa.me/919643335959?text=' + encodeURIComponent(message);
      }

      if (announce && liveNode && lastVariantId !== String(variant.id)) {
        liveNode.textContent = optionLabel + ', ' + money(price, currency) + ', ' +
          (variant.available ? 'available' : 'currently unavailable');
      }
      lastVariantId = String(variant.id);
      root.classList.remove('is-changing');
    };

    const scheduleSync = () => {
      root.classList.add('is-changing');
      window.clearTimeout(updateTimer);
      [0, 80, 240].forEach((delay) => window.setTimeout(() => sync(true), delay));
      updateTimer = window.setTimeout(() => root.classList.remove('is-changing'), 360);
    };

    form.addEventListener('change', scheduleSync);
    form.addEventListener('click', (event) => {
      if (event.target.closest('[data-swatch-item], [data-dropdown-item]')) scheduleSync();
    });

    const swatchRoot = form.querySelector('.t4s-swatch');
    if (swatchRoot) {
      new MutationObserver(() => sync(false)).observe(swatchRoot, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class']
      });
    }

    if (shareButton) {
      shareButton.addEventListener('click', async () => {
        const variant = currentVariant();
        const optionLabel = variant ? (variant.options || []).map(humanize).join(' · ') : '';
        const shareData = {
          title: root.dataset.productTitle || document.title,
          text: optionLabel ? 'Selected option: ' + optionLabel : '',
          url: location.href
        };
        try {
          if (navigator.share) {
            await navigator.share(shareData);
          } else if (navigator.clipboard) {
            await navigator.clipboard.writeText(location.href);
            const original = shareButton.innerHTML;
            shareButton.textContent = 'Link copied';
            window.setTimeout(() => { shareButton.innerHTML = original; }, 1600);
          }
        } catch (_) {}
      });
    }

    sync(false);
  };

  const initializeAll = (scope) => {
    (scope || document).querySelectorAll(SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
